import { useEffect, useRef, useState, type RefObject } from 'react'
import { LoaderCircle, LogOut, Mic, MicOff, Video, VideoOff } from 'lucide-react'
import { Badge, Button, ConfirmDialog, IconButton, Notice } from '@/components/play'
import { apiFailure } from '@/lib/apiFailure'
import { waitWords } from '@/lib/retryWait'
import type { StagePresence } from '@/lib/watch/useStagePresence'
import { cn } from '@/lib/utils'
import { LiveOn, type LiveOnStage } from './HostDock'
import './watch.css'

export interface GuestDockProps {
  /** `useStagePresence()`'s result, live as a guest: the dock follows its state and calls its controls. */
  presence: StagePresence
  /** Leave stage went through: the page says so. */
  onLeft?(): void
  /** The dock's first control (the microphone), so the page can move focus here once the guest is on stage. */
  focusRef?: RefObject<HTMLButtonElement>
  /** The guest's stage when another is on screen: "Live on Shane’s stage" above the dock, and the way back (`LiveOn`). */
  liveOn?: LiveOnStage | null
  /** Classes for the dock's wrapper, for the page to place it. */
  className?: string
}

const LEAVE_FAILED = 'We couldn’t take you off the stage. Try again.'

/**
 * Why Leave stage didn't leave, for the dock's question and the page's leave guard alike: leaving is throttled with going
 * live (30 a minute), so a 429 says when to try again.
 */
export function leaveFailureMessage(failure: unknown) {
  const refused = apiFailure(failure)
  const wait = refused.status === 429 ? waitWords(refused.retryAfterMs) : null
  return wait ? `That’s a lot of tries. Try again in ${wait}.` : LEAVE_FAILED
}

/**
 * A guest's controls while they are on a stage (the prototype's guest dock): microphone, camera (none when they joined
 * with the microphone alone) and **Leave stage**, which asks first. The host dock's pill and status region, without a
 * host's tools: no inviting, no calling cameras, no End. Shown only once LiveKit has published (`state === 'live'`);
 * joining shows in the camera check. Reconnecting and a live problem (a camera that stopped) sit under it, in one polite
 * live region that is always there.
 */
export function GuestDock({ presence, onLeft, focusRef, liveOn = null, className }: GuestDockProps) {
  const leaveRef = useRef<HTMLButtonElement>(null)
  const [asking, setAsking] = useState(false)
  const [failure, setFailure] = useState(LEAVE_FAILED)
  const live = presence.state === 'live'

  // Off the stage some other way (a host took them off): the question closes for good.
  useEffect(() => {
    if (!live) setAsking(false)
  }, [live])

  if (!live) return null

  return (
    <div className={cn('watch-host', className)}>
      {liveOn && <LiveOn stage={liveOn} />}
      <div className="watch-dock" role="group" aria-label="Guest controls" data-state="live">
        <IconButton ref={focusRef} variant="ghost" label="Mute" aria-pressed={!presence.micOn} onClick={() => void presence.toggleMic()}>
          {presence.micOn ? <Mic /> : <MicOff />}
        </IconButton>
        {presence.camera && (
          <IconButton variant="ghost" label="Turn camera off" aria-pressed={!presence.cameraOn} onClick={() => void presence.toggleCamera()}>
            {presence.cameraOn ? <Video /> : <VideoOff />}
          </IconButton>
        )}
        <Button ref={leaveRef} variant="danger" onClick={() => setAsking(true)}>
          <LogOut aria-hidden="true" />
          Leave stage
        </Button>
      </div>

      <div className="watch-host__status" aria-live="polite" aria-atomic="true">
        {presence.reconnecting && (
          <Badge tone="warning" icon={<LoaderCircle />} className="watch-host__busy">
            Reconnecting…
          </Badge>
        )}
        {presence.error && (
          <Notice tone="error" aria-live="off" className="watch-host__notice">
            {presence.error.message}
          </Notice>
        )}
      </div>

      <ConfirmDialog
        open={asking && live}
        onOpenChange={setAsking}
        title="Leave the stage?"
        description="Your audience stops seeing and hearing you. You keep watching."
        confirmLabel="Leave stage"
        onConfirm={() =>
          presence.leave().then(
            () => onLeft?.(),
            (refused: unknown) => {
              setFailure(leaveFailureMessage(refused))
              throw refused
            },
          )
        }
        failureMessage={failure}
        finalFocus={leaveRef}
      />
    </div>
  )
}
